import {createContext, useCallback, useMemo} from 'react'
import {useSelector} from 'react-redux'

export const UserPermissionsContext = createContext()

const UserPermissionsProvider = ({children}) => {
  const {currentSubscriptionData: {accessObject = {}} = {}} = useSelector(state => state.billing)
  const {userPermissions} = useSelector(state => state.roles)

  const permissions = useMemo(() => userPermissions, [userPermissions])

  // Feature Access
  const advanceFeaturesAccess = useMemo(() => {
    const temp = {...accessObject.advanceFeatures}
    return temp
  }, [accessObject])

  const analyticsAccess = useMemo(() => {
    const temp = {...accessObject.analytics}
    return temp
  }, [accessObject])

  const commonAccess = useMemo(() => {
    const temp = {...accessObject.common}
    return temp
  }, [accessObject])

  const conversationAccess = useMemo(() => {
    const temp = {...accessObject.conversation}
    return temp
  }, [accessObject])

  const customerManagementAccess = useMemo(() => {
    const temp = {...accessObject.customerManagement}
    return temp
  }, [accessObject])

  const integrationsAccess = useMemo(() => {
    const temp = {...accessObject.integrations}
    return temp
  }, [accessObject])

  const knowledgeBaseAccess = useMemo(() => {
    const temp = {...accessObject.knowledgeBase}
    return temp
  }, [accessObject])

  const omniChannelsAccess = useMemo(() => {
    const temp = {...accessObject.omniChannels}
    return temp
  }, [accessObject])

  const supportAccess = useMemo(() => {
    const temp = {...accessObject.support}
    return temp
  }, [accessObject])

  const teamManagementAccess = useMemo(() => {
    const temp = {...accessObject.teamManagement}
    return temp
  }, [accessObject])

  const workflowAutomationAccess = useMemo(() => {
    const temp = {...accessObject.workflowAutomation}
    return temp
  }, [accessObject])

  const hasFeatureAccess = useCallback(
    (category, type) => {
      return accessObject?.[category]?.[type] || false
    },
    [accessObject]
  )

  // Role Permissions
  const conversationsPermissions = useMemo(() => {
    const temp = {}
    temp.create_ticket = permissions?.conversations?.includes('create_ticket')
    temp.manage_conversation = permissions?.conversations?.includes('manage_conversation')
    return temp
  }, [permissions])

  const docsPermissions = useMemo(() => {
    const temp = {}
    temp.manage_articles = permissions?.docs?.includes('manage_articles')
    temp.manage_categories = permissions?.docs?.includes('manage_categories')
    temp.customize_docs = permissions?.docs?.includes('customize_docs')
    temp.view_articles = permissions?.docs?.includes('view_articles')
    temp.docs_reports = permissions?.docs?.includes('docs_reports')
    return temp
  }, [permissions])

  const tasksPermissions = useMemo(() => {
    const temp = {}
    temp.manage_tasks = permissions?.tasks?.includes('manage_tasks')
    temp.manage_task_templates = permissions?.tasks?.includes('manage_task_templates')
    temp.manage_task_automations = permissions?.tasks?.includes('manage_task_automations')
    return temp
  }, [permissions])

  const assetsPermissions = useMemo(() => {
    const temp = {}
    temp.manage_assets = permissions?.assets?.includes('manage_assets')
    temp.manage_custom_fields = permissions?.assets?.includes('manage_custom_fields')
    temp.import_assets = permissions?.assets?.includes('import_assets')
    return temp
  }, [permissions])

  const clientPortalPermissions = useMemo(() => {
    const temp = {}
    temp.manage_announcements = permissions?.client_portal?.includes('manage_announcements')
    temp.customization = permissions?.client_portal?.includes('customization')
    temp.settings = permissions?.client_portal?.includes('settings')
    return temp
  }, [permissions])

  const statusPagePermissions = useMemo(() => {
    const temp = {}
    temp['manage_incident_&_maintainance'] = permissions?.status_page?.includes('manage_incident_&_maintainance')
    temp.manage_service = permissions?.status_page?.includes('manage_service')
    temp.customization = permissions?.status_page?.includes('customization')
    temp.settings = permissions?.status_page?.includes('settings')
    return temp
  }, [permissions])

  const contactsPermissions = useMemo(() => {
    const temp = {}
    temp.manage_contacts = permissions?.contacts?.includes('manage_contacts')
    temp.import_contacts = permissions?.contacts?.includes('import_contacts')
    temp.export_contacts = permissions?.contacts?.includes('export_contacts')
    return temp
  }, [permissions])

  const reportsPermissions = useMemo(() => {
    const temp = {}
    temp.inbox_reports = permissions?.reports?.includes('inbox_reports')
    temp.task_reports = permissions?.reports?.includes('task_reports')
    temp.asset_reports = permissions?.reports?.includes('asset_reports')
    temp.status_page_report = permissions?.reports?.includes('status_page_report')
    temp.performance_reporting = permissions?.reports?.includes('performance_reporting')
    return temp
  }, [permissions])

  const organizationPermissions = useMemo(() => {
    const temp = {}
    temp.manage_business_hours = permissions?.organization?.includes('manage_business_hours')
    return temp
  }, [permissions])

  const channelsPermissions = useMemo(() => {
    const temp = {}
    temp.manage_emails = permissions?.channels?.includes('manage_emails')
    temp.manage_support_widget = permissions?.channels?.includes('manage_support_widget')
    temp.manage_contact_form = permissions?.channels?.includes('manage_contact_form')
    temp.manage_facebook = permissions?.channels?.includes('manage_facebook')
    temp.manage_whatsapp = permissions?.channels?.includes('manage_whatsapp')
    temp.manage_twitter = permissions?.channels?.includes('manage_twitter')
    return temp
  }, [permissions])

  const workPermissions = useMemo(() => {
    const temp = {}
    temp.manage_tags = permissions?.work?.includes('manage_tags')
    temp.manage_email_templates = permissions?.work?.includes('manage_email_templates')
    temp.manage_automation_rules = permissions?.work?.includes('manage_automation_rules')
    return temp
  }, [permissions])

  const productivityPermissions = useMemo(() => {
    const temp = {}
    temp.manage_sla = permissions?.productivity?.includes('manage_sla')
    temp.manage_customer_satisfaction = permissions?.productivity?.includes('manage_customer_satisfaction')
    temp.audit_logs = permissions?.productivity?.includes('audit_logs')
    return temp
  }, [permissions])

  const integrationsPermissions = useMemo(() => {
    const temp = {}
    temp.manage_integrations = permissions?.integrations?.includes('manage_integrations')
    temp.view_data_from_integrations = permissions?.integrations?.includes('view_data_from_integrations')
    temp.perform_actions = permissions?.integrations?.includes('perform_actions')
    return temp
  }, [permissions])

  const hasPermissions = useCallback(
    (category, type) => {
      return permissions?.[category]?.includes(type) || false
    },
    [permissions]
  )

  return (
    <UserPermissionsContext.Provider
      value={{
        // Feature Access
        advanceFeaturesAccess,
        analyticsAccess,
        commonAccess,
        conversationAccess,
        integrationsAccess,
        knowledgeBaseAccess,
        customerManagementAccess,
        supportAccess,
        omniChannelsAccess,
        teamManagementAccess,
        workflowAutomationAccess,
        hasFeatureAccess,
        // Role Permissions
        conversationsPermissions,
        docsPermissions,
        tasksPermissions,
        assetsPermissions,
        clientPortalPermissions,
        statusPagePermissions,
        contactsPermissions,
        reportsPermissions,
        organizationPermissions,
        channelsPermissions,
        workPermissions,
        productivityPermissions,
        integrationsPermissions,
        hasPermissions,
      }}
    >
      {children}
    </UserPermissionsContext.Provider>
  )
}

export default UserPermissionsProvider
